/**
 * Fieldset — Design-system-compliant fieldset component.
 * Figma: https://www.figma.com/design/8MHmVdfscl5k2kpTqf4UXX/%F0%9F%93%81-102.-Moveo-%C2%B7-DS?node-id=9116-8425&m=dev
 * Linear: https://linear.app/moveo-ai/issue/MOV-14420/standardize-fieldsets-component-with-design-system
 *
 * - Title rendered outside the card (via FieldsetTitle, primary/secondary variants)
 * - Footer blends with card (no divider, no muted background)
 * - Max-width 576px (max-w-xl)
 */

import Link from '@/components/atoms/Link/Link';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

import FieldsetCard from './FieldsetCard';
import FieldsetFooter from './FieldsetFooter';
import FieldsetFooterAction from './FieldsetFooterAction';
import FieldsetTitle from './FieldsetTitle';

type LinkType = { label: string; url: string };

export interface FieldsetProps {
  children?: React.ReactNode;
  className?: string;
  title?: string | React.ReactNode;
  titleVariant?: 'primary' | 'secondary';
  subtitle?: React.ReactNode;
  isLoading?: boolean;
  fullWidth?: boolean;
  tooltip?: string;
  externalLink?: LinkType;
  hasBadge?: boolean;
  integrationActive?: boolean;
  overflown?: boolean;
}

const Fieldset: React.FC<FieldsetProps> = ({
  children,
  className,
  title,
  titleVariant = 'secondary',
  subtitle,
  isLoading,
  fullWidth,
  tooltip,
  externalLink,
  hasBadge,
  integrationActive,
  overflown,
}) => {
  const renderTitle = () => {
    if (!title) {
      return null;
    }

    if (typeof title !== 'string') {
      return title;
    }

    return (
      <FieldsetTitle
        variant={titleVariant}
        hasBadge={hasBadge}
        integrationActive={integrationActive}
        tooltip={tooltip}
      >
        {title}
      </FieldsetTitle>
    );
  };

  return (
    <div
      className={cn('flex w-full max-w-xl flex-col gap-2', fullWidth && 'max-w-full', className)}
    >
      {renderTitle()}
      <FieldsetCard overflown={overflown}>
        {isLoading ? (
          <>
            <Skeleton className="mb-4 h-5 w-1/2" />
            <Skeleton className="mb-4 h-5 w-full" />
            <FieldsetFooter>
              <Skeleton className="h-4 w-14" />
              <FieldsetFooterAction>
                <Skeleton className="h-9 w-16" />
              </FieldsetFooterAction>
            </FieldsetFooter>
          </>
        ) : (
          <>
            {subtitle && (
              <div className="mb-4 pr-8 text-sm text-muted-foreground">
                {subtitle}
                {externalLink && (
                  <>
                    <br />
                    <Link key={externalLink.label} href={externalLink.url} external>
                      {externalLink.label}
                    </Link>
                  </>
                )}
              </div>
            )}
            {children}
          </>
        )}
      </FieldsetCard>
    </div>
  );
};

export default Fieldset;
